import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RealEstateAgentOutlinedIcon from '@mui/icons-material/RealEstateAgentOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import {
  formatFlowProfileLabel,
  getLoanApplicationHubTitle,
  getLoanHubFlowMeta,
  type LoanHubFlowBucket,
} from '@/utils/loanApplicationHub';
import type { LoanApplication } from '@/types/LoanApplicationType';

const statusColor = (
  s: string
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  switch (s) {
    case 'approved':
      return 'success';
    case 'declined':
    case 'withdrawn':
      return 'default';
    case 'submitted':
    case 'in_review':
      return 'warning';
    default:
      return 'info';
  }
};

function FlowIcon({ bucket }: { bucket: LoanHubFlowBucket }) {
  const sx = { fontSize: 28, opacity: 0.9 };
  switch (bucket) {
    case 'refinance':
      return <SwapHorizOutlinedIcon sx={sx} />;
    case 'investor':
      return <TrendingUpOutlinedIcon sx={sx} />;
    default:
      return <HomeOutlinedIcon sx={sx} />;
  }
}

type Props = {
  application: LoanApplication;
  onOpen: () => void;
};

export function LoanHubApplicationCard({ application, onOpen }: Props) {
  const theme = useTheme();
  const meta = getLoanHubFlowMeta(application);
  const title = getLoanApplicationHubTitle(application);
  const programLabel = application.productType?.toUpperCase() ?? '—';
  const flowLabel = formatFlowProfileLabel(application.flowProfile);
  const updated = application.updatedAt
    ? new Date(application.updatedAt).toLocaleDateString()
    : '—';

  const accent =
    meta.bucket === 'refinance'
      ? theme.palette.info.main
      : meta.bucket === 'investor'
      ? theme.palette.secondary.main
      : theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        borderColor: alpha(accent, 0.35),
        bgcolor: alpha(accent, 0.03),
        '&:hover': {
          borderColor: accent,
          boxShadow: `0 4px 16px ${alpha(accent, 0.12)}`,
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: alpha(accent, 0.12),
            color: accent,
          }}
        >
          <FlowIcon bucket={meta.bucket} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Chip label={meta.label} size="small" color={meta.chipColor} />
            <Chip label={programLabel} size="small" variant="outlined" />
            {flowLabel && flowLabel !== meta.label ? (
              <Chip label={flowLabel} size="small" variant="outlined" />
            ) : null}
            <Chip
              label={application.status?.replace(/_/g, ' ') ?? 'draft'}
              size="small"
              color={statusColor(application.status ?? 'draft')}
              variant="outlined"
            />
          </Stack>

          <Typography fontWeight={700} noWrap title={title}>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {meta.propertyHandling}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            sx={{ mt: 1 }}
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Updated {updated}
            </Typography>
            {application.lifecycleStage &&
            application.lifecycleStage !== 'shopping' ? (
              <Typography variant="caption" color="text.secondary">
                · {application.lifecycleStage.replace(/_/g, ' ')}
              </Typography>
            ) : null}
            {meta.bucket !== 'refinance' &&
            application.linkedPurchaseProductIds &&
            application.linkedPurchaseProductIds.length === 1 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                · <RealEstateAgentOutlinedIcon sx={{ fontSize: 14 }} /> Open
                from property dashboard for listing details
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <ChevronRightIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
      </Stack>
    </Paper>
  );
}
